interface PromoRules {
  excluded_product_ids: number[];
  included_product_ids: number[];
}

interface Tier {
  price_range: number[];
  percent: number;
}

interface PromoDiscount {
  flat_percent: number | null;
  tiered: Tier[] | null;
}

export interface Promo {
  id: number;
  code: string;
  description: string;
  rules: PromoRules;
  discounts: PromoDiscount;
  starts_at: string;
  expires_at: string;
}

export type Price = number | string;

const getTieredDiscount = (price: Price, promo: Promo) => {
  const tieredDiscounts = promo?.discounts?.tiered;
  if (price && tieredDiscounts && tieredDiscounts.length > 0) {
    return tieredDiscounts.reduce((acc, discount) => {
      const { price_range, percent } = discount;
      const min = +price_range[0];
      const max = price_range[1] !== null ? +price_range[1] : Infinity;
      if (+price >= min && +price < max) {
        acc = percent;
      }
      return acc;
    }, 0);
  }
  return 0;
};

const getPercentDiscount = (
  price: Price,
  promo?: Promo | null,
): number => {
  const flatPercentDiscount = promo?.discounts?.flat_percent;
  const tieredDiscount = promo?.discounts?.tiered?.length;
  if (flatPercentDiscount) {
    return flatPercentDiscount;
  }
  if (tieredDiscount && price) {
    return getTieredDiscount(price, promo);
  }
  return 0;
};

/**
 * Format total price as whole number without trailing zeros or show price with cents
 */
const formatPrice = (price: number): string => {
  return Number.isInteger(price) ? price.toString() : (+price).toFixed(2);
};

const calculateDiscountedPrice = (
  price: Price,
  percentDiscount: number,
): number => {
  if (percentDiscount !== 0) {
    const totalDiscount = +price * (+percentDiscount / 100);
    return +price - +totalDiscount;
  }
  return +price;
};

/**
 * Returns the first promo that has a start and end time encompassing
 * the current time.
 * @param promos an array of possible advertised promos
 */
export const getActivePromo = (promos: Promo[]): Promo | undefined => {
  const now = new Date();
  return promos.find((promo) => {
    const startsAt = new Date(promo.starts_at);
    const expiresAt = new Date(promo.expires_at);
    return startsAt <= now && now <= expiresAt;
  });
};

/**
 * Returns true if the given promo is applicable to a given product. It is
 * applicable if the promo is explicitely included in the promo rules, or
 * is at least not explicitely excluded.
 * @param productId ID of the given product
 * @param promo the promo to check
 * @param promotionable attribute describing whether the given product can have promos applied in general
 */
export const promoAppliesToProduct = (
  productId: number,
  promo: Promo,
  promotionable?: boolean,
): boolean => {
  return (
    promotionable != false &&
    (!promo.rules.excluded_product_ids.includes(productId) &&
      (promo.rules.included_product_ids.includes(productId) ||
        promo.rules.included_product_ids.length === 0))
  );
};

/**
 * Return base price, or discounted price if promo is applicable
 */
export const discountedPrice = (basePrice: Price, promo?: Promo | null): string => {
  const percent = getPercentDiscount(basePrice, promo);
  const discountedPrice = calculateDiscountedPrice(
    basePrice,
    percent,
  );

  return formatPrice(discountedPrice);
};
